import {
  TEMPLATE_GROUP_ASSET_DRIVEN,
  TEMPLATE_GROUP_TEMPLATES,
} from '~/constants/template';

export type TemplatePickerOption = {
  value: string;
  label: string;
  $group?: string;
  behavior?: 'static' | 'asset_rules' | null;
};

export type GroupedTemplateSelectItem = {
  $group?: string;
  id: string;
  name: string;
};

/**
 * Filter templates by a whitespace-stripped, case-insensitive query and
 * split matches into static vs asset-driven groups.
 */
export function filterTemplateOptions<T extends TemplatePickerOption>(
  templateOptions: T[],
  query: string
): { staticTemplates: T[]; assetDrivenTemplates: T[]; all: T[] } {
  const searchQuery = query.toLowerCase().replace(/\s+/g, '');
  const matchingOptions =
    query === ''
      ? templateOptions
      : templateOptions.filter((option) =>
          option.label.toLowerCase().replace(/\s+/g, '').includes(searchQuery)
        );

  const staticTemplates = matchingOptions.filter(
    (opt) => opt.behavior !== 'asset_rules'
  );
  const assetDrivenTemplates = matchingOptions.filter(
    (opt) => opt.behavior === 'asset_rules'
  );

  return { staticTemplates, assetDrivenTemplates, all: matchingOptions };
}

/**
 * Group template select options into Asset-Driven vs Templates sections.
 * Existing `$group` values are preserved. When no behavior map is provided,
 * original `$group` values are kept (including undefined).
 */
export function groupTemplateSelectOptions(
  options: TemplatePickerOption[],
  behaviorById?: Map<string, 'static' | 'asset_rules'>
): GroupedTemplateSelectItem[] {
  if (!behaviorById || behaviorById.size === 0) {
    return options.map((opt) => ({
      $group: opt.$group,
      id: opt.value,
      name: opt.label,
    }));
  }

  return options.map((opt) => {
    const isAssetDriven = behaviorById.get(opt.value) === 'asset_rules';
    const group =
      opt.$group ||
      (isAssetDriven ? TEMPLATE_GROUP_ASSET_DRIVEN : TEMPLATE_GROUP_TEMPLATES);

    return {
      $group: group,
      id: opt.value,
      name: opt.label,
    };
  });
}
